import { type FC } from 'react';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import {
    trackEditorialClick,
    trackEditorialImpression,
} from '../../HpSharedEditorialModule/helpers/EditorialTracking.ts';

import { type HpSharedItemsGridEditorialCollectionItem } from '../types.ts';
import styles from './HpSharedItemsGridEditorialCollection.module.css';

type HpSharedItemsGridEditorialCollectionProps = {
    item: HpSharedItemsGridEditorialCollectionItem;
};

const HpSharedItemsGridEditorialCollection: FC<HpSharedItemsGridEditorialCollectionProps> = ({
    item,
}) => {
    const trackingItem = { linkData: { path: item.linkPath } };
    const header = item.header?.trim();

    return (
        <div
            className={classnames(styles.container, dibsCss.bgSand100, dibsCss.roundedXsmall)}
            data-tn="hp-shared-items-grid-editorial-collection"
        >
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackEditorialImpression({
                            item: trackingItem,
                            index: item.editorialItemIndex,
                            editorialModuleOrder: item.editorialModuleOrder,
                        });
                    }
                }}
            />
            <Link
                href={item.linkPath}
                className={styles.link}
                onClick={() =>
                    trackEditorialClick({
                        item: trackingItem,
                        index: item.editorialItemIndex,
                        editorialModuleOrder: item.editorialModuleOrder,
                    })
                }
            >
                <img
                    alt={item.title}
                    src={item.imageUrl}
                    srcSet={getSrcsetString(item.imageUrl, [500, 640])}
                    sizes="(max-width: 568px) 500px, 640px"
                    className={classnames(
                        styles.imageContainer,
                        dibsCss.wFull,
                        dibsCss.objectCover,
                        dibsCss.roundedTXsmall
                    )}
                />
                <div
                    className={classnames(
                        styles.textContainer,
                        dibsCss.flex,
                        dibsCss.flexCol,
                        dibsCss.pySmall,
                        dibsCss.prMedium,
                        dibsCss.plSmall
                    )}
                >
                    <div>
                        <div className={dibsCss.sassyFontBodyTypeHeavy}>
                            {header || (
                                <FormattedMessage
                                    id="abm.HpSharedItemsGridEditorialCollection.trendingNowTitle"
                                    defaultMessage="Trending Now"
                                />
                            )}
                        </div>
                        <div
                            className={classnames(
                                dibsCss.sassyFontHeaderLarge,
                                dibsCss.mbXxsmall,
                                dibsCss.lineClamp2
                            )}
                        >
                            {item.title}
                        </div>
                    </div>
                    <div
                        className={classnames(
                            dibsCss.sassyFontBodyMediumHeavy,
                            styles.linkText,
                            dibsCss.mtAuto,
                            dibsCss.mbXsmall
                        )}
                    >
                        {item.cta}
                    </div>
                </div>
            </Link>
        </div>
    );
};

export default HpSharedItemsGridEditorialCollection;
